import React, { useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { AppButton } from '@pro-sto/ui';
import { authStore, shiftDraftStore } from '@/stores';
import './ShiftAddEmployeePicker.css';

const employeeName = (lastName: string, firstName: string) =>
  `${lastName} ${firstName}`.trim();

const AddEmployeeIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="6" cy="5" r="2.15" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M2.4 12.6c.45-2.05 1.85-3.1 3.6-3.1s3.15 1.05 3.6 3.1"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path d="M12 7.2v5.2M9.4 9.8h5.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ChevronDownIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 6.5L8 10.5L12 6.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const placeAddMenu = (root: HTMLElement, menu: HTMLElement): 'up' | 'down' | 'hidden' => {
  const rect = root.getBoundingClientRect();
  const header = document.querySelector('.app-header');
  const topLimit = header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 0;
  const visible =
    rect.bottom > topLimit &&
    rect.top < window.innerHeight &&
    rect.right > 0 &&
    rect.left < window.innerWidth;
  if (!visible) return 'hidden';

  const gap = 4;
  const cap = 240;
  const spaceBelow = window.innerHeight - rect.bottom - gap - 12;
  const spaceAbove = rect.top - topLimit - gap - 12;
  const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
  const available = Math.max(120, Math.min(cap, openUp ? spaceAbove : spaceBelow));
  menu.style.width = `${rect.width}px`;
  menu.style.left = `${rect.left}px`;
  menu.style.maxHeight = `${available}px`;
  if (openUp) {
    menu.style.top = 'auto';
    menu.style.bottom = `${window.innerHeight - rect.top + gap}px`;
  } else {
    menu.style.bottom = 'auto';
    menu.style.top = `${rect.bottom + gap}px`;
  }
  return openUp ? 'up' : 'down';
};

export interface ShiftAddEmployeePickerProps {
  takenIds: Set<string>;
  onPick: (employeeUuid: string) => void;
  disabled?: boolean;
  'data-testid'?: string;
  loadingTestId?: string;
  retryTestId?: string;
}

export const ShiftAddEmployeePicker: React.FC<ShiftAddEmployeePickerProps> = observer(
  ({
    takenIds,
    onPick,
    disabled = false,
    'data-testid': dataTestId,
    loadingTestId,
    retryTestId,
  }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const blurTimer = useRef<number | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    const showList = () => {
      if (disabled) return;
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
      setOpen(true);
    };

    const options = shiftDraftStore.employees
      .filter((employee) => !takenIds.has(employee.uuid))
      .map((employee) => ({
        value: employee.uuid,
        label: employeeName(employee.last_name, employee.first_name),
      }));
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;

    const pick = (employeeUuid: string) => {
      onPick(employeeUuid);
      setQuery('');
      setOpen(false);
    };

    useLayoutEffect(() => {
      if (!open || disabled) return;
      const root = rootRef.current;
      const menu = menuRef.current;
      if (!root || !menu) return;
      const place = () => {
        const direction = placeAddMenu(root, menu);
        if (direction === 'hidden') {
          setOpen(false);
          setQuery('');
        }
      };
      const onOutside = (event: MouseEvent) => {
        const target = event.target;
        if (target instanceof Node && root.contains(target)) return;
        setOpen(false);
        setQuery('');
      };
      place();
      window.addEventListener('scroll', place, true);
      window.addEventListener('resize', place);
      document.addEventListener('mousedown', onOutside);
      return () => {
        window.removeEventListener('scroll', place, true);
        window.removeEventListener('resize', place);
        document.removeEventListener('mousedown', onOutside);
      };
    }, [open, disabled, matches.length]);

    useLayoutEffect(() => {
      if (disabled && open) {
        setOpen(false);
        setQuery('');
      }
    }, [disabled, open]);

    return (
      <div
        ref={rootRef}
        className={`shift-add-employee${open ? ' shift-add-employee_open' : ''}${disabled ? ' shift-add-employee_disabled' : ''}`}
      >
        <span className="shift-add-employee__icon" aria-hidden="true">
          <AddEmployeeIcon />
        </span>
        <input
          className="shift-add-employee__input"
          value={query}
          title={query || undefined}
          placeholder="Добавить сотрудника"
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
          aria-autocomplete="list"
          data-testid={dataTestId}
          onClick={showList}
          onFocus={showList}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => {
              setOpen(false);
              setQuery('');
            }, 120);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            showList();
          }}
        />
        <button
          type="button"
          className={`shift-add-employee__chevron${open ? ' shift-add-employee__chevron_up' : ''}`}
          aria-label="Показать сотрудников"
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
            if (disabled) return;
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
            setOpen((value) => !value);
          }}
        >
          <ChevronDownIcon />
        </button>
        {open && !disabled ? (
          <ul ref={menuRef} className="shift-add-employee__menu" role="listbox">
            {shiftDraftStore.employeesLoading ? (
              <li className="shift-add-employee__empty" data-testid={loadingTestId}>
                Загрузка...
              </li>
            ) : shiftDraftStore.employeesError ? (
              <li
                className="shift-add-employee__retry"
                onMouseDown={(event) => event.preventDefault()}
              >
                <span title={shiftDraftStore.employeesError}>{shiftDraftStore.employeesError}</span>
                <AppButton
                  variant="secondary"
                  onClick={() => {
                    const center = authStore.user?.service_center_uuid;
                    if (center) void shiftDraftStore.loadEmployees(center, { force: true });
                  }}
                  disabled={shiftDraftStore.employeesLoading}
                  data-testid={retryTestId}
                >
                  Повторить
                </AppButton>
              </li>
            ) : matches.length > 0 ? (
              matches.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    className="shift-add-employee__option"
                    title={option.label}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => pick(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            ) : (
              <li className="shift-add-employee__empty">Нет подходящих сотрудников</li>
            )}
          </ul>
        ) : null}
      </div>
    );
  },
);

ShiftAddEmployeePicker.displayName = 'ShiftAddEmployeePicker';
